import { createFileRoute, Link } from "@tanstack/react-router";
import { CheckCircle2, Phone, Mail, ClipboardCheck, MessageCircle, CalendarCheck } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { Button } from "@/components/ui/button";
import { HeroBackground } from "@/components/HeroBackground";
import { company } from "@/data/site";
import contactHero from "@/assets/heroes/areas-served.jpg";

export const Route = createFileRoute("/thank-you")({
  head: () => ({
    meta: [
      { title: "Thank You – Coastal Surface Experts" },
      { name: "description", content: "Thanks for reaching out to Coastal Surface Experts. We'll be in touch shortly with your free pressure washing quote." },
      { name: "robots", content: "noindex, follow" },
      { property: "og:title", content: "Thank You – Coastal Surface Experts" },
      { property: "og:description", content: "Your request is in. We'll be in touch shortly." },
    ],
    links: [{ rel: "canonical", href: "https://mckay.steadyautogrowthdemo.com/thank-you" }],
  }),
  component: ThankYouPage,
});

const steps = [
  {
    icon: ClipboardCheck,
    title: "We review your details",
    body: "Our team looks over your property info within minutes during business hours.",
  },
  {
    icon: MessageCircle,
    title: "We reach out with a quote",
    body: "Expect a call or email – usually within the hour – with clear pricing and next steps.",
  },
  {
    icon: CalendarCheck,
    title: "We schedule the wash",
    body: "Pick a time that works. Licensed, insured crews arrive on time and get it done right.",
  },
];

function ThankYouPage() {
  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden bg-gradient-hero text-primary-foreground">
          <HeroBackground src={contactHero} alt="Coastal Surface Experts crew ready to help" />
          <div className="relative mx-auto flex min-h-[85vh] max-w-4xl flex-col justify-center px-4 py-20 text-center sm:px-6 sm:py-28 lg:px-8 lg:py-36">
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-glow">
              <CheckCircle2 className="h-11 w-11" strokeWidth={2.25} />
            </div>
            <h1 className="mt-6 text-5xl font-extrabold sm:text-6xl text-balance">
              Thanks – we've got your request!
            </h1>
            <p className="mx-auto mt-5 max-w-2xl text-lg text-primary-foreground/85">
              A member of our team will reach out shortly, usually within the hour during business hours ({company.hours}).
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-elegant">
                <a href={company.phoneHref}>
                  <Phone className="mr-2 h-4 w-4" /> Call {company.phone}
                </a>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/">Back to home</Link>
              </Button>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-sm font-bold uppercase tracking-wider text-accent">What happens next</div>
            <h2 className="mt-2 text-3xl font-extrabold text-primary sm:text-4xl">A simple, no-pressure process</h2>
          </div>
          <div className="mt-10 grid gap-6 sm:grid-cols-3">
            {steps.map((s, i) => (
              <div key={s.title} className="rounded-2xl border border-border bg-card p-6 shadow-elegant">
                <div className="flex items-center gap-3">
                  <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent/15 text-accent">
                    <s.icon className="h-5 w-5" />
                  </div>
                  <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Step {i + 1}</div>
                </div>
                <h3 className="mt-4 text-lg font-extrabold text-primary">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-6xl px-4 pb-20 sm:px-6 lg:px-8">
          <div className="grid gap-6 rounded-3xl bg-gradient-hero p-8 text-primary-foreground sm:p-12 lg:grid-cols-2 lg:items-center">
            <div>
              <h2 className="text-3xl font-extrabold sm:text-4xl text-balance">While you wait – explore our services</h2>
              <p className="mt-3 text-primary-foreground/85">
                From soft-wash house cleaning to commercial storefronts, we cover it all across Broward County.
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:justify-end">
              <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-elegant">
                <Link to="/residential">Residential services</Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-primary-foreground/40 bg-transparent text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <Link to="/commercial">Commercial services</Link>
              </Button>
            </div>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2">
            <a href={company.phoneHref} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition hover:border-accent">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent/15 text-accent">
                <Phone className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Prefer to talk?</div>
                <div className="text-base font-extrabold text-primary">{company.phone}</div>
              </div>
            </a>
            <a href={`mailto:${company.email}`} className="flex items-center gap-4 rounded-2xl border border-border bg-card p-5 transition hover:border-accent">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent/15 text-accent">
                <Mail className="h-5 w-5" />
              </div>
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Email us</div>
                <div className="text-base font-extrabold text-primary">{company.email}</div>
              </div>
            </a>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
